import { collections, firestoreHelpers } from '@/lib/firebase';
import type { Transaction, Coupon } from '@/types';

export const paymentService = {
  // Create transaction
  async createTransaction(transactionData: Partial<Transaction>): Promise<string> {
    const newTransaction: Partial<Transaction> = {
      ...transactionData,
      status: 'pending',
      createdAt: new Date(),
    };
    
    return await firestoreHelpers.createDocument(collections.transactions, newTransaction);
  },

  // Get transaction by ID
  async getTransaction(transactionId: string): Promise<Transaction | null> {
    return await firestoreHelpers.getDocument<Transaction>(collections.transactions, transactionId);
  },

  // Update transaction status
  async updateTransactionStatus(
    transactionId: string, 
    status: Transaction['status']
  ): Promise<void> {
    await firestoreHelpers.updateDocument(collections.transactions, transactionId, {
      status,
      completedAt: status === 'completed' ? new Date() : null,
    });
  },

  // Get user transactions
  async getUserTransactions(userId: string, limitCount?: number): Promise<Transaction[]> {
    return await firestoreHelpers.queryDocuments<Transaction>(
      collections.transactions,
      [{ field: 'userId', operator: '==', value: userId }],
      'createdAt',
      'desc',
      limitCount
    );
  },

  // Get ride transactions
  async getRideTransactions(rideId: string): Promise<Transaction[]> {
    return await firestoreHelpers.queryDocuments<Transaction>(
      collections.transactions,
      [{ field: 'rideId', operator: '==', value: rideId }],
      'createdAt',
      'desc'
    );
  },

  // Get all transactions (for admin)
  async getAllTransactions(limitCount?: number): Promise<Transaction[]> {
    return await firestoreHelpers.queryDocuments<Transaction>(
      collections.transactions,
      [],
      'createdAt',
      'desc',
      limitCount
    );
  },

  // Get today's transactions (for admin)
  async getTodayTransactions(): Promise<Transaction[]> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return await firestoreHelpers.queryDocuments<Transaction>(
      collections.transactions,
      [{ field: 'createdAt', operator: '>=', value: today }],
      'createdAt',
      'desc'
    );
  },

  // Get total revenue (for admin)
  async getTotalRevenue(): Promise<number> {
    const transactions = await firestoreHelpers.queryDocuments<Transaction>(
      collections.transactions,
      [
        { field: 'status', operator: '==', value: 'completed' },
        { field: 'type', operator: 'in', value: ['ride_payment', 'wallet_deposit'] }
      ]
    );
    
    return transactions.reduce((sum, t) => sum + t.amount, 0);
  },

  // Get today's revenue (for admin)
  async getTodayRevenue(): Promise<number> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const transactions = await firestoreHelpers.queryDocuments<Transaction>(
      collections.transactions,
      [
        { field: 'status', operator: '==', value: 'completed' },
        { field: 'type', operator: 'in', value: ['ride_payment', 'wallet_deposit'] },
        { field: 'createdAt', operator: '>=', value: today }
      ]
    );
    
    return transactions.reduce((sum, t) => sum + t.amount, 0);
  },

  // ============ COUPON SERVICES ============

  // Create coupon (admin only)
  async createCoupon(couponData: Partial<Coupon>): Promise<string> {
    const newCoupon: Partial<Coupon> = {
      ...couponData,
      usageCount: 0,
      isActive: true,
      createdAt: new Date(),
    };
    
    return await firestoreHelpers.createDocument(collections.coupons, newCoupon);
  },

  // Get coupon by code
  async getCouponByCode(code: string): Promise<Coupon | null> {
    const coupons = await firestoreHelpers.queryDocuments<Coupon>(
      collections.coupons,
      [{ field: 'code', operator: '==', value: code.toUpperCase() }]
    );
    return coupons.length > 0 ? coupons[0] : null;
  },

  // Validate coupon
  async validateCoupon(code: string, rideAmount: number): Promise<{ valid: boolean; coupon?: Coupon; message?: string }> {
    const coupon = await this.getCouponByCode(code);
    
    if (!coupon) {
      return { valid: false, message: 'كود الخصم غير موجود' };
    }
    
    if (!coupon.isActive) {
      return { valid: false, message: 'كود الخصم غير نشط' };
    }
    
    const now = new Date();
    if (now < coupon.startDate) {
      return { valid: false, message: 'كود الخصم غير متاح بعد' };
    }
    
    if (now > coupon.endDate) {
      return { valid: false, message: 'كود الخصم منتهي الصلاحية' };
    }
    
    if (coupon.usageCount >= coupon.usageLimit) {
      return { valid: false, message: 'تم استنفاد عدد مرات استخدام كود الخصم' };
    }
    
    if (coupon.minRideAmount && rideAmount < coupon.minRideAmount) {
      return { valid: false, message: `الحد الأدنى للرحلة ${coupon.minRideAmount} جنيه` };
    }
    
    return { valid: true, coupon };
  },

  // Apply coupon
  async applyCoupon(couponId: string): Promise<void> {
    const coupon = await firestoreHelpers.getDocument<Coupon>(collections.coupons, couponId);
    if (coupon) {
      await firestoreHelpers.updateDocument(collections.coupons, couponId, {
        usageCount: coupon.usageCount + 1,
      });
    }
  },

  // Get all coupons (admin only)
  async getAllCoupons(): Promise<Coupon[]> {
    return await firestoreHelpers.queryDocuments<Coupon>(
      collections.coupons,
      [],
      'createdAt',
      'desc'
    );
  },

  // Get active coupons
  async getActiveCoupons(): Promise<Coupon[]> {
    const now = new Date();
    return await firestoreHelpers.queryDocuments<Coupon>(
      collections.coupons,
      [
        { field: 'isActive', operator: '==', value: true },
        { field: 'startDate', operator: '<=', value: now },
        { field: 'endDate', operator: '>=', value: now },
      ],
      'createdAt',
      'desc'
    );
  },

  // Update coupon (admin only)
  async updateCoupon(couponId: string, data: Partial<Coupon>): Promise<void> {
    await firestoreHelpers.updateDocument(collections.coupons, couponId, data);
  },

  // Delete coupon (admin only)
  async deleteCoupon(couponId: string): Promise<void> {
    await firestoreHelpers.deleteDocument(collections.coupons, couponId);
  },

  // ============ PAYMENT METHODS ============

  // Process Bankak payment
  async processBankakPayment(
    accountNumber: string, 
    amount: number, 
    description: string
  ): Promise<{ success: boolean; transactionId?: string; message?: string }> {
    // In production, integrate with Bankak API
    // For demo, simulate success
    return {
      success: true,
      transactionId: `BNK-${Date.now()}`,
      message: 'تمت عملية الدفع بنجاح',
    };
  },

  // Process Fawry payment
  async processFawryPayment(
    phoneNumber: string, 
    amount: number, 
    description: string
  ): Promise<{ success: boolean; transactionId?: string; message?: string }> {
    // In production, integrate with Fawry API
    // For demo, simulate success
    return {
      success: true,
      transactionId: `FWR-${Date.now()}`,
      message: 'تمت عملية الدفع بنجاح',
    };
  },

  // Process Sadad payment
  async processSadadPayment(
    accountNumber: string, 
    amount: number, 
    description: string
  ): Promise<{ success: boolean; transactionId?: string; message?: string }> {
    // In production, integrate with Sadad API
    // For demo, simulate success
    return {
      success: true,
      transactionId: `SDD-${Date.now()}`,
      message: 'تمت عملية الدفع بنجاح',
    };
  },

  // Calculate discount
  calculateDiscount(amount: number, coupon: Coupon): number {
    let discount = 0;
    
    if (coupon.type === 'percentage') {
      discount = amount * (coupon.value / 100);
    } else {
      discount = coupon.value;
    }
    
    // Apply max discount if set
    if (coupon.maxDiscount && discount > coupon.maxDiscount) {
      discount = coupon.maxDiscount;
    }
    
    return Math.round(discount);
  }
};
